import type { Document } from "@models";
import { getDocVersionRevisionNumber } from "src/utils/documents";
import type {
  DynamicNodeOption,
  DynamicNodeOwner,
} from "./dynamic-node-context";

const getDidRevisionNumber = (did: string, documentId: string) => {
  const revisionMarker = `-${documentId}-R`;
  const revisionStart = did.indexOf(revisionMarker);
  if (revisionStart === -1) return null;

  const revision = did.slice(revisionStart + revisionMarker.length);
  const match = /^(\d+)(?:-|$)/.exec(revision);
  return match ? Number(match[1]) : null;
};

const getReferenceVersionId = (document: Document, did: string) => {
  const versions = [...document.versions].sort(
    (left, right) => right.createdAt.getTime() - left.createdAt.getTime()
  );
  const revisionNumber = getDidRevisionNumber(did, document.id);
  const matchingVersion =
    revisionNumber === null
      ? undefined
      : versions.find(
          (version) =>
            getDocVersionRevisionNumber(version, document.versions) ===
            revisionNumber
        );
  const newestDraft = versions.find(
    (version) => version.revision === null && !version.readyForApproval
  );

  return matchingVersion?.id ?? newestDraft?.id;
};

const toDocumentReferenceOwner = (
  document: Document,
  did: string
): DynamicNodeOwner => ({
  title: document.title,
  documentId: document.id,
  deviceId: document.deviceId ?? null,
  versionId: getReferenceVersionId(document, did),
});

export const getDocumentReferenceOptions = (
  contextValues: Record<string, unknown>,
  documents: readonly Document[],
  excludedDocumentId?: string
): DynamicNodeOption[] =>
  documents.flatMap((document) => {
    if (document.id === excludedDocumentId) {
      return [];
    }

    const key = `did-${document.id}`;
    const value = contextValues[key];
    if (value === null || value === undefined || typeof value === "object") {
      return [];
    }

    const did = String(value);
    return [
      {
        key,
        label: document.title,
        subtitle: did,
        owner: toDocumentReferenceOwner(document, did),
      },
    ];
  });

export const resolveDocumentReferenceOwner = (
  key: string,
  did: string,
  documents: readonly Document[]
): DynamicNodeOwner | undefined => {
  const reference = key.slice("did-".length);
  const document =
    documents.find(({ id }) => id === reference) ??
    documents.find(({ id }) => did.includes(`-${id}-`));

  return document ? toDocumentReferenceOwner(document, did) : undefined;
};
